import React, { useState, useEffect } from "react";
import { Button, Table, Spinner } from "react-bootstrap";
import TablePaginationBar from "../components/TablePaginationBar";
import { useNavigate, Link } from "react-router-dom";
import useFetchData from "../hooks/useFetchData";
import { config } from "../config/config";
import { getactiveproviders } from "../config/url";
import axios from "axios";
import { checkPermissionByKey } from "../utils/checkPermissionByKey";
import { useSelector } from "react-redux";
import { FaSearch } from "react-icons/fa";

const TeamMembers = () => {
  const [currentMembers, setCurrentMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [membersPerPage] = useState(10);
  const [roles, setRoles] = useState([]);
  const [viewAll, setViewAll] = useState(false);
  const [baseMembers, setBaseMembers] = useState([]);
  const user = useSelector((state) => state?.user);

  const businessId = user.user?.businessId;
  const businessIdentifier = user.user?.businessIdentifier;
  const role = user.user?.role;

  // Fetch data on initial load
  const dataResponse = useFetchData(
    `${getactiveproviders}?businessId=${businessId}&businessIdentifier=${businessIdentifier}`,
    config
  );
  const data = checkPermissionByKey(
    user,
    "teamMembersManagement",
    "TeamMembersListAccess"
  )
    ? dataResponse
    : null;

  // Update base members when data loads
  useEffect(() => {
    // Only update base members when loading is complete
    if (data?.loading === false) {
      if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
        setBaseMembers(data.data);
      } else {
        // Explicitly set empty array when response comes back with no members or null/undefined
        setBaseMembers([]);
      }
    }
    // Only depend on loading state and array length to avoid infinite loops
    // Using data?.data?.length instead of data?.data to prevent re-renders when array reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.loading, data?.data?.length]);

  // Filter and search - runs when filters or base data changes
  useEffect(() => {
    let updatedMembers = [...baseMembers];

    // Search
    if (search) {
      updatedMembers = updatedMembers.filter((member) => {
        const fullName =
          `${member.firstname} ${member.lastname}`.toLowerCase();
        const roleName = member.Role?.name?.toLowerCase() || "";
        return (
          fullName.includes(search.toLowerCase()) ||
          member.email.toLowerCase().includes(search.toLowerCase()) ||
          roleName.includes(search.toLowerCase())
        );
      });
    }

    // Filter by Active/Inactive status
    if (filterStatus) {
      updatedMembers = updatedMembers.filter(
        (member) => member.isactive === (filterStatus === "Active")
      );
    }

    // Filter by Role
    if (filterRole) {
      updatedMembers = updatedMembers.filter((member) => {
        return String(member.role || member.Role?.id) === filterRole;
      });
    }

    setFilteredMembers(updatedMembers);
    // Reset to first page whenever filters change
    setCurrentPage(1);
  }, [search, filterRole, filterStatus, baseMembers]);

  const getRoles = async () => {
    try {
      const businessId = user.user?.businessId;
      const businessIdentifier = user.user?.businessIdentifier;

      const request = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/role/getRoleNames?businessId=${businessId}&businessIdentifier=${businessIdentifier}`,
        config
      );
      if (request.data.success) {
        setRoles(request.data.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getRoles();
    // getRoles is stable and doesn't need to be in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pagination logic
  useEffect(() => {
    if (!viewAll) {
      const indexOfLastMember = currentPage * membersPerPage;
      const indexOfFirstMember = indexOfLastMember - membersPerPage;
      setCurrentMembers(
        filteredMembers.slice(indexOfFirstMember, indexOfLastMember)
      );
    } else {
      setCurrentMembers(filteredMembers); // Show all members when viewAll is true
    }
    // membersPerPage is a constant, doesn't need to be in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filteredMembers, viewAll]);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const navigate = useNavigate();

  return (
    <div className="app-page">
      <div className="bg-white rounded shadow-sm">
        <div className="app-page-toolbar">
          <h2 className="app-page-title">Team Members</h2>
          <div className="app-page-toolbar-filters">
            <div className="app-filter-search-wrap">
              <FaSearch className="app-filter-search-icon" />
              <input
                type="text"
                className="form-control app-filter-search"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: "220px" }}
              />
            </div>
            <select
              className="form-select app-filter-select"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              style={{ width: "140px" }}
            >
              <option value="">Role</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            <select
              className="form-select app-filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ width: "140px" }}
            >
              <option value="">Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
            {role === "business" &&
              checkPermissionByKey(user, "teamMembersManagement", "AddTeamMember") && (
                <Link to="/add-member">
                  <Button className="app-filter-action-btn" variant="primary">
                    Add New
                  </Button>
                </Link>
              )}
          </div>
        </div>
        {data?.loading === true ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-3 text-muted">Loading team members...</div>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center p-4">No team members found.</div>
        ) : (
          <>
            <Table
              responsive
              hover
              className="mb-0"
              style={{
                borderCollapse: "separate",
                borderSpacing: 0,
              }}
            >
              <thead
                style={{
                  backgroundColor: "#064260",
                  color: "white",
                  textAlign: "center",
                  height: "50px",
                }}
              >
                <tr>
                  <th>ID</th>
                  <th style={{ width: "300px" }}>Name</th>
                  <th style={{ width: "300px" }}>Email</th>
                  <th style={{ width: "300px" }}>Role</th>
                  <th>Status</th>
                  <th style={{ width: "150px" }}>Action</th>
                </tr>
              </thead>
              <tbody
                style={{
                  textAlign: "center",
                  color: "#575757",
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {currentMembers.length === 0 ? (
                  <tr>
                    <td colSpan="6">No team members found.</td>
                  </tr>
                ) : (
                  currentMembers.map((member, index) => (
                    <tr
                      key={member.id}
                      style={{
                        backgroundColor: "white",
                      }}
                    >
                      {/* <td>{console.log(member, "===member==")}</td> */}
                      <td>
                        {(index + 1 + (currentPage - 1) * membersPerPage)
                          .toString()
                          .padStart(4, "0")}
                      </td>
                      <td>{member.firstname + " " + member.lastname}</td>
                      <td>{member.email}</td>
                      <td>{member?.Role?.name}</td>
                      <td>{member.isactive ? "Active" : "Inactive"}</td>
                      <td>
                        {checkPermissionByKey(
                          user,
                          "teamMembersManagement",
                          "ViewTeamMemberProfile"
                        ) && (
                          <Button
                            className="app-view-btn"
                            onClick={() => {
                              navigate(`/team-member/${member._id}`, {
                                state: { member },
                              });
                            }}
                          >
                            View
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            {viewAll ? (
              <div
                className="d-flex justify-content-end align-items-center py-3 pe-0"
                style={{
                  gap: "15px",
                  paddingBottom: "46px",
                }}
              >
                <Button
                  onClick={() => {
                    setViewAll(!viewAll);
                    setCurrentPage(1);
                  }}
                  style={{
                    backgroundColor: "#27B99A",
                    border: "none",
                    borderRadius: "4px",
                    width: "100px",
                  }}
                >
                  Paginate
                </Button>
              </div>
            ) : (
              <TablePaginationBar
                currentPage={currentPage}
                totalItems={filteredMembers.length}
                rowsPerPage={membersPerPage}
                onPageChange={paginate}
                className="pe-0"
                style={{
                  gap: "15px",
                  paddingBottom: "46px",
                }}
                leftExtra={
                  <Button
                    onClick={() => {
                      setViewAll(!viewAll);
                      setCurrentPage(1);
                    }}
                    style={{
                      backgroundColor: "#27B99A",
                      border: "none",
                      borderRadius: "4px",
                      width: "100px",
                    }}
                  >
                    View All
                  </Button>
                }
              />
            )}
          </>
        )}

        <div
          className="d-flex justify-content-end"
          style={{ paddingRight: "4%" }}
        ></div>
      </div>
    </div>
  );
};

export default TeamMembers;
